/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2021, Bulb Inc. For more information, please email contact@hellobulb.com.
 *          _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
 *
*/

/**
 * @module bulb-progress/bulb-upload/bulbuploadprogress
 */

import {
  Plugin,
  type Editor,
  type ModelElement,
  DowncastConversionApi,
  ViewElement,
  ViewDowncastWriter,
  LocaleTranslate,
  ViewContainerElement,
} from 'ckeditor5';
import uploadingPlaceholder from '../theme/icons/image_placeholder.svg';
import { cancelBusyIndicator, toTitleCase } from '../../utils';
import {
  clean,
  getUIElement,
  initializeProgressBar,
  startAppearEffect,
  updateProgressBar,
} from '../bulbprogressutils';

/**
 * The image upload progress plugin.
 * It shows a placeholder when the image is read from the disk and a progress bar while the image is uploading.
 *
 * @extends Plugin
 */
export default class BulbUploadProgress extends Plugin {

  placeholder: string;

  /**
   * @inheritDoc
   */
  constructor(editor: Editor) {
    super(editor);

    /**
     * The image placeholder that is displayed before real image data can be accessed.
     *
     * @protected
     * @member {String} #placeholder
     */
    this.placeholder = `data:image/svg+xml;utf8,${encodeURIComponent(uploadingPlaceholder)}`;
  }

  /**
   * @inheritDoc
   */
  public static get pluginName() {
    return 'BulbUploadProgress' as const;
  }

  /**
   * @inheritDoc
   */
  public init(): void {
    const { editor } = this;
    const { bulb } = editor as any;

    // Checking if bulb property exist in editor. this will be undefined when cke is not enabled.
    if (bulb) {
      // Initializing assetLibraryUploadEvent.
      bulb.service.ckeStateService.initializeAssetLibraryUploadEvent();

      // Initialize the event that is going to listen for the response from our client.
      bulb.service.ckeStateService.assetLibraryUploadEvent.subscribe((event: CustomEvent) => {
        if (event.type === 'assetLibraryUploadStarted') {
          editor.execute('bulbInsertUploadPlaceholder', event.detail);
        }
      });
    }

    // TODO: Create our own event listener here that still has access to the downcast conversionApi.
    editor.editing.downcastDispatcher.on(
      'insert:bulbUploadPlaceholder',
      (evt, data: { item: ModelElement }, conversionApi: DowncastConversionApi) => {
        this.uploadStatusChange(data, conversionApi);
      },
    );
  }

  /**
   * @inheritDoc
   */
  public destroy(): void {
    // Complete the observable.
    (this.editor as any).bulb?.service.ckeStateService.assetLibraryUploadEvent.complete();
  }

  /**
   * This method is called each time the image `uploadStatus` attribute is changed.
   *
   * @param {Object} data Additional information about the change.
   * @param {module:engine/conversion/downcastdispatcher~DowncastConversionApi} conversionApi
   */
  private uploadStatusChange(data: { item: ModelElement }, conversionApi: DowncastConversionApi) {
    const { editor } = this;
    const { bulb } = editor as any;
    const { model } = editor;
    const modelElement = data.item;

    const { placeholder } = this;

    const viewFigure: ViewElement = editor.editing.mapper.toViewElement(modelElement);
    const viewWriter: ViewDowncastWriter = conversionApi.writer;

    if (bulb.service.undoRedo.undoRedoPlaceholderProgress) {
      editor.execute(bulb.service.undoRedo.action);
      return;
    }

    const insertionPosition = model.createPositionBefore(modelElement);

    const ASSET_TYPE = modelElement.getAttribute('asset-type') as string === 'ATTACHED_FILE'
      ? 'FILE' : modelElement.getAttribute('asset-type') as string;
    const ASSET_NAME = modelElement.getAttribute('asset-name');
    // eslint-disable-next-line no-nested-ternary
    const ASSET_ICON = ['IMAGE', 'VIDEO'].includes(ASSET_TYPE) ? `fa-${ASSET_TYPE.toLowerCase()}`
      : ASSET_TYPE === 'AUDIO' ? 'fa-microphone' : 'fa-file';

    const uploadSucceeded = modelElement.getAttribute('upload-succeeded');

    // Finalized asset information:
    const assetInformation = {
      assetName: ASSET_NAME,
      assetIcon: ASSET_ICON,
      assetType: toTitleCase(ASSET_TYPE),
      uploadSucceeded,
    };

    // Only proceed if the upload is currently happening and isn't disturbed. If it is, something happen and the
    // Upload failed.
    if (uploadSucceeded) {
      // Start appear effect if needed - see https://github.com/ckeditor/ckeditor5-image/issues/191.
      startAppearEffect(viewFigure, viewWriter);
      BulbUploadProgress.showPlaceholder(placeholder, viewFigure, viewWriter, assetInformation, editor);
      let progressBar;
      if (assetInformation.assetType === 'Video' || assetInformation.assetType === 'Audio') {
        const uploadProgressBar = {
          viewFigure,
          viewWriter,
          view: editor.editing.view,
          progress: 0,
          translate: editor.t,
          transcoding: false,
          instantPlay: false,
          assetType: assetInformation.assetType,
        };
        progressBar = initializeProgressBar(uploadProgressBar);
      }

      /**
       * Event listen that updates the progress bar accordingly.
       */
      window.addEventListener('assetLibraryUploadProgress', (event: any) => {
        const ASSET_ID = viewFigure.getAttribute('asset-id');
        const UPLOAD_ASSET_ID = event.detail.data.entry.id;
        const { progress } = event.detail;
        // Only do these things if the ID of the asset that was uploaded was the same as the asset that was being
        // uploaded this entire time. You can upload multiple blocks at a time, this makes them all correlate
        // to themselves.
        if (ASSET_ID === UPLOAD_ASSET_ID
          && (assetInformation.assetType === 'Video' || assetInformation.assetType === 'Audio')) {
          updateProgressBar(editor.editing.view, progressBar, progress);
        }
      });

      window.addEventListener('assetLibraryUploadCompleted', (event: any) => {
        const ASSET_ID = viewFigure.getAttribute('asset-id');
        const UPLOAD_ASSET_ID = event.detail.trackingId;

        // Only do these things if the ID of the asset that was uploaded was the same as the asset that was being
        // uploaded this entire time. You can upload multiple blocks at a time, this makes them all correlate
        // to themselves.
        if (ASSET_ID === UPLOAD_ASSET_ID) {
          BulbUploadProgress.showCompleteIcon(viewFigure, viewWriter, editor, assetInformation);
          // Do a quick 3 second timeout to allow the showCompleteIcon function to fire and show up.
          setTimeout(() => {
            clean(viewFigure, viewWriter);
            switch (ASSET_TYPE) {
              case 'VIDEO':
              case 'AUDIO': {
                const transcoderDetails = event.detail;
                editor.execute('insertBulbTranscoder', transcoderDetails, modelElement, insertionPosition);

                break;
              }

              case 'FILE': {
                // Handle the attached file block differently depending on what type it is :
                const attachedFile = event.detail.contentBlock;
                const attachFileBlockType = event.detail.contentBlock.blockType;
                editor.execute(
                  attachFileBlockType === 'ATTACHED_FILE' ? 'insertAttachedFile' : 'insertEmbed',
                  attachedFile,

                  modelElement,

                  insertionPosition,
                );
                cancelBusyIndicator(editor);

                break;
              }

              case 'IMAGE': {
                const imageBlock = event.detail.contentBlock;
                editor.execute('insertBulbImage', imageBlock, modelElement, insertionPosition, event.detail.nextIndex);
                cancelBusyIndicator(editor);
                break;
              }

              default:
                console.error('I should never reach here, this means the asset type does not exist.');
                break;
            }
          }, 3000);
        }
      });
    } else {
      BulbUploadProgress.showPlaceholder(placeholder, viewFigure, viewWriter, assetInformation, editor);
    }
  }

  /**
   * IMPORTANT : This method was taken from cke's source file : imageuploadprogress.js.
   *
   * Shows placeholder together with infinite progress bar on given image figure.
   *
   * @param {String} placeholder Data-uri with a svg placeholder.
   * @param {module:engine/view/containerelement~ContainerElement} viewFigure
   * @param {module:engine/view/downcastwriter~ViewDowncastWriter} writer
   * ####### bulb changes #######
   * @param assetInformation Current asset uploading information
   * ####### end bulb changes #######
   * @param editor Current editor instance.
   *
   * @private
   */
  private static showPlaceholder(
    placeholder: string,
    viewFigure,
    writer: ViewDowncastWriter,
    assetInformation,
    editor: Editor,
  ): void {
    // This means the asset is successfullying uploading. If this value isn't present something went wrong.
    if (assetInformation.uploadSucceeded) {
      if (!viewFigure.hasClass('ck-image-upload-placeholder')) {
        writer.addClass('ck-image-upload-placeholder', viewFigure);
      }

      const viewImg = viewFigure.getChild(0);

      if (viewImg.getAttribute('src') !== placeholder) {
        writer.setAttribute('src', placeholder, viewImg);
      }

      if (!getUIElement(viewFigure, 'placeholder')) {
        writer.insert(
          writer.createPositionAfter(viewImg),
          BulbUploadProgress.createPlaceholder(writer, assetInformation, editor.t),
        );
      }
    } else {
      const viewImg = viewFigure.getChild(0);
      writer.insert(
        writer.createPositionAfter(viewImg),
        BulbUploadProgress.createUploadErrorPlaceholder(writer, assetInformation, editor),
      );
    }
  }

  /**
   * IMPORTANT : This method was taken from cke's source file : imageuploadprogress.js.
   * We've expanded off this method and made some major changes.
   *
   * Shows complete icon and hides after a certain amount of time.
   *
   * @param viewFigure viewFigure from editor.
   * @param writer ViewDowncastWriter instance from editor to change model / view.
   * @param editor current editor.
   * @param assetInformation current asset upload information.
   *
   * @private
   */
  private static showCompleteIcon(viewFigure: ViewElement, writer: ViewDowncastWriter, editor: Editor, assetInformation) {
    const { bulb } = editor as any;
    // Add the complete icon into the top right of the placeholder element.
    const completeIcon = writer.createEmptyElement('i', {
      class: 'completed-upload-icon far fa-check-circle',
    });

    // Change the placeholder text from "Uploading" to "Uploaded."
    const placeholderElement = getUIElement(viewFigure, 'placeholder');
    const statusElement = getUIElement(placeholderElement, 'uploadStatus');
    bulb.service.translate.translate(
      `content_block_type_${assetInformation.assetType.toUpperCase()}`,
    ).then((assetType) => {
      const uploadedText = editor.t('uploaded');
      const newStatusText = writer.createText(`${assetType} ${uploadedText}`);
      // Use the writer to make the new changes.
      editor.editing.view.change((downcastWriter: ViewDowncastWriter) => {
        downcastWriter.removeClass('text-ellipsis', statusElement);
        downcastWriter.remove(downcastWriter.createRangeOn(statusElement.getChild(0)));
        // Cancel button has been removed so no more required to remove below element
        // writer.remove(statusElement.getChild(0));
        downcastWriter.insert(downcastWriter.createPositionAt(statusElement, 'end'), newStatusText);
        if (assetInformation.assetType !== 'Audio' && assetInformation.assetType !== 'Video') {
          downcastWriter.insert(downcastWriter.createPositionAt(viewFigure, 'end'), completeIcon);
        }
      });
    });
  }

  /**
   *
   * IMPORTANT : This method was taken from cke's source file : imageuploadprogress.js.
   * We've expanded off this method and made some major changes.
   *
   * Create placeholder element using {@link module:engine/view/uielement~UIElement}.
   *
   * @param writer {module:engine/view/downcastwriter~ViewDowncastWriter} writer
   * ####### bulb changes #######
   * @param assetInformation Current information on uploading asset.
   * @param t editor translation object
   * ####### end bulb changes #######
   * @returns {module:engine/view/containerelement~ContainerElement}
   *
   * @private
   */
  private static createPlaceholder(writer: ViewDowncastWriter, assetInformation, t: LocaleTranslate): ViewContainerElement {
    const placeholderElement = writer.createContainerElement('div', {
      class: `ck-upload-placeholder-loader ${assetInformation.assetType === 'Audio' ? 'bulb-audio-placeholder' : ''}`,
    });

    // Create the icon and file-name inside of the placeholder.
    const iconElement = writer.createUIElement('icon', { class: `far ${assetInformation.assetIcon} bulb-upload-icon` });
    const fileNameElement = writer.createContainerElement('div', { class: 'bulb-upload-filename' });
    const statusElement = writer.createContainerElement('div', { class: 'bulb-progress-status' });
    const statusText = writer.createText(t(`uploading_${assetInformation.assetType.toLowerCase()}_inprogress`));
    const fileNameText = writer.createText(assetInformation.assetName);

    // Create UIElement for 'cancel' button:
    // const bulbUploadCancelElement = writer.createUIElement('div', {
    //   class: 'bulb-upload-cancel-button',
    // }, function (domDocument) {
    //   const domElement = this.toDomElement(domDocument);
    //   domElement.innerHTML = `<button id="cancel-upload"
    //                 class="bulb-upload-cancel"
    //                 onclick="( () => {
    //                 const customCancelEvent = new CustomEvent('cancelUploadBlock', {
    //                   detail: {
    //                     cancelUploadBlock : true
    //                   }
    //                 });
    //                 window.dispatchEvent( customCancelEvent );
    //                 })();">Cancel</button>`; // TODO CKE translation needed.
    //   return domElement;
    // });

    // Insert the new things into the upload block.
    writer.insert(writer.createPositionAt(statusElement, 0), statusText);
    writer.insert(writer.createPositionAt(fileNameElement, 0), fileNameText);
    writer.insert(writer.createPositionAt(placeholderElement, 'end'), iconElement);
    writer.insert(writer.createPositionAt(placeholderElement, 'end'), fileNameElement);
    writer.insert(writer.createPositionAt(placeholderElement, 'end'), statusElement);
    // FUTURE : For now removing cancel option on video upload
    // writer.insert(writer.createPositionAt(statusElement, 'end'), bulbUploadCancelElement);

    writer.setCustomProperty('placeholder', true, placeholderElement);
    writer.setCustomProperty('uploadStatus', true, statusElement);

    return placeholderElement;
  }

  /**
   * Create placeholder element using {@link module:engine/view/uielement~UIElement}.
   *
   * @param writer
   * @param assetInformation
   * @param editor Editor instance.
   *
   * @returns {module:engine/view/containerelement~ContainerElement}
   * @private
   */
  private static createUploadErrorPlaceholder(
    writer: ViewDowncastWriter,
    assetInformation,
    editor: Editor,
  ): ViewContainerElement {
    // Main container element to house how the 'failed' upload block will look like:
    const faileUploadPlaceholderElement = writer.createContainerElement('div', {
      class: 'ck-upload-failed-placeholder-loader',
    });

    // Create the icon and upload failed description text inside of the placeholder:
    const uploadFailedIconElement = writer.createUIElement(
      'icon',
      { class: 'fas fa-exclamation-triangle bulb-upload-icon' },
    );
    const uploadFailedTitleElement = writer.createContainerElement('div', { class: 'bulb-upload-failed-title' });
    const uploadFailedRetryElement = writer.createContainerElement('div', { class: 'bulb-upload-failed-retry' });
    const uploadFailedRetryText = writer.createText(editor.t('upload_retry'));
    const assetTypeTranslation = editor.t(`content_block_type_${assetInformation.assetType.toUpperCase()}`);
    const faildText = editor.t('upload_failed');
    const uploadFailedTitleText = writer.createText(`${assetTypeTranslation} ${faildText}`);

    // Create UIElement for 'abort' or 'retry' buttons:
    const bulbUploadFailedButtonsElement = writer.createUIElement('div', {
      class: 'bulb-upload-file-buttons',
    }, function (domDocument) {
      const domElement = this.toDomElement(domDocument);
      domElement.innerHTML = `<button id="delete-failed-upload"
                    class="bulb-upload-abort"
                    onclick="( () => {
                    const customDeleteEvent = new CustomEvent('deleteFailedUploadBlock', {
                      detail: {
                        deleteFailedUploadBlock : true
                      }
                    });
                    window.dispatchEvent( customDeleteEvent );
                    })();">No</button>
                <button class="bulb-upload-retry"
                    onclick="( () => {
                    const customRetryEvent = new CustomEvent('retryFailedUploadBlock', {
                      detail: {
                        retryFailedUploadBlock : true
                      }
                    });
                    window.dispatchEvent( customRetryEvent );
                    })()">Yes</button>`; // TODO CKE translation needed.
      return domElement;
    });

    // Adding upload failed retry text to the upload failed container element:
    writer.insert(writer.createPositionAt(uploadFailedRetryElement, 0), uploadFailedRetryText);
    writer.insert(writer.createPositionAt(uploadFailedTitleElement, 0), uploadFailedTitleText);

    // Adding all nodes to the placeholder container element created above:
    writer.insert(writer.createPositionAt(faileUploadPlaceholderElement, 'end'), uploadFailedIconElement);
    writer.insert(writer.createPositionAt(faileUploadPlaceholderElement, 'end'), uploadFailedTitleElement);
    writer.insert(writer.createPositionAt(faileUploadPlaceholderElement, 'end'), uploadFailedRetryElement);
    writer.insert(writer.createPositionAt(faileUploadPlaceholderElement, 'end'), bulbUploadFailedButtonsElement);

    // Set certain values on the placeholder and uploadstatus properties of the block.
    writer.setCustomProperty('placeholder', true, faileUploadPlaceholderElement);

    return faileUploadPlaceholderElement;
  }
}
